/**
 * @file: WiredBubbleMenu.tsx
 *
 * The long-press action-menu overlay host. Subscribes the conversation's
 * `bubbleMenu` state (set when a bubble is long-pressed, carrying its
 * window-coord rect + a clone of its body) and renders, over a blurred
 * backdrop: the lifted bubble clone in place + the action menu beside it.
 *
 * Teleported to the app-root `portal` slot via `<Fill>` so it renders at the
 * WINDOW origin — the bubble measured itself with `measureInWindow` (window
 * coords), and the inverted message list's `scaleY(-1)` flip would corrupt
 * positioning if the overlay rendered inside the list's tree. App-wide host +
 * window-coord rect = correct placement on every platform.
 */

import { MessageActionMenu } from '@euler/app/flows/chat/components/message/MessageActionMenu';
import { MessageEmojiGridPanel } from '@euler/app/flows/chat/components/message/MessageEmojiGridPanel';
import { MessageReactionPill } from '@euler/app/flows/chat/components/message/MessageReactionPill';
import { useChatConversation } from '@euler/app/flows/chat/lib/conversation/context';
import {
  BUBBLE_PRESS_SCALE,
  BubbleMenuTarget,
} from '@euler/app/flows/chat/lib/conversation/hooks/useBubbleMenu';
import { BlurView } from '@euler/components/blur-view/BlurView';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatMsg } from '@euler/model/chat';
import { useBehaviorSubject } from '@euler/utils/hooks';
import MaskedView from '@react-native-masked-view/masked-view';
import { LinearGradient } from 'expo-linear-gradient';
import { MotiView } from 'moti';
import { FC, memo, useEffect, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Fill } from 'react-slot-fill';

/** Gap between the bubble and the menu. */
const MENU_GAP = 8;
/** Matches MessageActionMenu's fixed card width (rpx(216)). */
const MENU_RPX_WIDTH = 216;

const clamp = (v: number, min: number, max: number) =>
  Math.min(Math.max(v, min), Math.max(min, max));

export const WiredBubbleMenu: FC = memo(() => {
  const { bubbleMenu, onReplyTo, onEditRequested, onDeleteRequested, onReact } =
    useChatConversation().coordinator;
  const [target] = useBehaviorSubject(bubbleMenu.state$);
  return (
    <Fill name="portal">
      {target ? (
        <BubbleMenuOverlay
          target={target}
          onDismiss={bubbleMenu.dismiss}
          onReply={onReplyTo}
          onEdit={onEditRequested}
          onDelete={onDeleteRequested}
          onReact={onReact}
        />
      ) : null}
    </Fill>
  );
});

const BubbleMenuOverlay: FC<{
  target: BubbleMenuTarget;
  onDismiss: () => void;
  onReply: (message: ChatMsg) => void;
  onEdit: (message: ChatMsg) => void;
  onDelete: (message: ChatMsg) => void;
  onReact: (message: ChatMsg, emoji: string) => void;
}> = ({ target, onDismiss, onReply, onEdit, onDelete, onReact }) => {
  const { rpx } = useResponsiveContext();
  const win = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const { rect, isMine, message, clone, maxWidth } = target;
  // Which emojis the viewer already reacted with (tinted in the pill). A
  // snapshot from when the menu opened — the overlay is short-lived, so it
  // isn't kept live against `reaction.changed` echoes.
  const myReacted = new Set(
    (message.kind === 'remote' ? message.reactions ?? [] : [])
      .filter(r => r.reactedByMe)
      .map(r => r.emoji),
  );

  // Web: swallow the browser's native context menu anywhere on the overlay so
  // a right-click while it's open can't pop the Chrome menu over it.
  const rootRef = useRef<View>(null);
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const node = rootRef.current as unknown as HTMLElement | null;
    if (!node) return;
    const onContextMenu = (e: Event) => e.preventDefault();
    node.addEventListener('contextmenu', onContextMenu);
    return () => node.removeEventListener('contextmenu', onContextMenu);
  }, []);

  // Tapping the pill's chevron expands it into the full emoji grid (replacing
  // the pill + action menu until dismissed).
  const [expanded, setExpanded] = useState(false);

  const menuWidth = rpx(MENU_RPX_WIDTH);
  // The pill (above the bubble) + the menu measure their own size; until then
  // they stay hidden so any reposition happens off-screen (no visible jump).
  const [pillSize, setPillSize] = useState({ width: 0, height: 0 });
  const [menuSize, setMenuSize] = useState({ width: 0, height: 0 });
  const pillMeasured = pillSize.height > 0;
  const menuMeasured = menuSize.height > 0;

  const topLimit = insets.top + rpx(8);
  const bottomLimit = win.height - insets.bottom - rpx(8);
  // Where the pill wants to sit: just above the bubble.
  const pillAboveBubble = Math.max(
    topLimit,
    rect.y - pillSize.height - MENU_GAP,
  );

  // Menu placement, in preference order:
  //  1. below the bubble (its bottom is on-screen);
  //  2. else stacked ABOVE the pill (top→bottom: menu, pill, bubble);
  //  3. else — a bubble too tall for either ("tall"):
  //     · native → pill on top, menu anchored at the bottom, and the bubble
  //       fades into the backdrop where the menu overlaps it (see the mask);
  //     · web → MaskedView is unreliable, so fall back to stacking pill+menu
  //       from the top.
  const belowTop = rect.y + rect.height + MENU_GAP;
  const fitsBelow = !menuMeasured || belowTop + menuSize.height <= bottomLimit;
  const fitsAbovePill =
    !menuMeasured || pillAboveBubble - MENU_GAP - menuSize.height >= topLimit;
  const tall = !fitsBelow && !fitsAbovePill;
  const useGradient = tall && Platform.OS !== 'web';

  const { pillTop, menuTop } = ((): { pillTop: number; menuTop: number } => {
    if (fitsBelow) {
      return { pillTop: pillAboveBubble, menuTop: belowTop };
    }
    if (fitsAbovePill) {
      return {
        pillTop: pillAboveBubble,
        menuTop: pillAboveBubble - MENU_GAP - menuSize.height,
      };
    }
    if (useGradient) {
      // Pill hugs the bubble's top; menu anchored at the bottom. The bubble
      // fades into the backdrop over the band where the menu overlaps it.
      return {
        pillTop: pillAboveBubble,
        menuTop: bottomLimit - menuSize.height,
      };
    }
    return {
      pillTop: topLimit,
      menuTop: topLimit + pillSize.height + MENU_GAP,
    };
  })();

  // Fade the bubble clone into the backdrop where the bottom-anchored menu
  // overlaps it, so the menu reads cleanly over the fade (mapped from window
  // coords into the clone's local space; only once both are measured). The
  // band is generous so the dissolve is clearly visible, not a hard edge.
  const cloneHeight = Math.max(rect.height, 1);
  const fadeBand = rpx(140);
  const fadeEnd = clamp((menuTop - rect.y) / cloneHeight, 0.02, 1);
  const fadeStart = clamp(fadeEnd - fadeBand / cloneHeight, 0, fadeEnd - 0.01);
  const cloneMask =
    useGradient && pillMeasured && menuMeasured ? (
      <LinearGradient
        style={StyleSheet.absoluteFill}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        colors={['white', 'white', 'transparent', 'transparent']}
        locations={[0, fadeStart, fadeEnd, 1]}
      />
    ) : null;

  const pillLeft = clamp(
    isMine ? rect.x + rect.width - pillSize.width : rect.x,
    rpx(8),
    win.width - pillSize.width - rpx(8),
  );
  const menuLeft = clamp(
    isMine ? rect.x + rect.width - menuWidth : rect.x,
    rpx(8),
    win.width - menuWidth - rpx(8),
  );

  // Expanded emoji grid: near-full-width, filling the room above the bubble
  // (capped + floored so it always has a usable, scrollable height).
  const gridWidth = win.width - rpx(32);
  const gridHeight = clamp(
    rect.y - MENU_GAP - (insets.top + rpx(8)),
    rpx(140),
    rpx(300),
  );
  const gridTop = Math.max(insets.top + rpx(8), rect.y - MENU_GAP - gridHeight);
  const gridLeft = rpx(16);

  const close = (after?: () => void) => {
    onDismiss();
    after?.();
  };

  return (
    <View ref={rootRef} style={StyleSheet.absoluteFill}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onDismiss}>
        <BlurView style={StyleSheet.absoluteFill} intensity={60} tint="dark" />
        <View
          style={[
            StyleSheet.absoluteFill,
            { backgroundColor: 'rgba(0,0,0,0.45)' },
          ]}
        />
      </Pressable>

      {/* The pressed bubble, lifted in place over the backdrop at its measured
          rect (bubble-only — no avatar/status). Pops in with the same scale as
          the menu. */}
      <MotiView
        pointerEvents="none"
        from={{ scale: BUBBLE_PRESS_SCALE }}
        animate={{ scale: 1 }}
        transition={{ type: 'timing', duration: 140 }}
        style={{
          position: 'absolute',
          left: rect.x,
          top: rect.y,
          // Wrap at the source's column width (content-sized), so the bubble
          // breaks line-for-line and shrinks to the same final width.
          maxWidth,
        }}
      >
        {cloneMask ? (
          <MaskedView style={{ flexShrink: 1 }} maskElement={cloneMask}>
            {clone}
          </MaskedView>
        ) : (
          clone
        )}
      </MotiView>

      {/* Collapsed: quick-reaction pill (above) + action menu (below). */}
      {!expanded ? (
        <>
          <MotiView
            onLayout={e =>
              setPillSize({
                width: e.nativeEvent.layout.width,
                height: e.nativeEvent.layout.height,
              })
            }
            from={{ opacity: 0, scale: 0.92 }}
            animate={{
              opacity: pillMeasured ? 1 : 0,
              scale: pillMeasured ? 1 : 0.92,
            }}
            transition={{ type: 'timing', duration: 140 }}
            style={{ position: 'absolute', top: pillTop, left: pillLeft }}
          >
            <MessageReactionPill
              myReacted={myReacted}
              onReact={emoji => close(() => onReact(message, emoji))}
              onExpand={() => setExpanded(true)}
            />
          </MotiView>

          <MotiView
            onLayout={e =>
              setMenuSize({
                width: e.nativeEvent.layout.width,
                height: e.nativeEvent.layout.height,
              })
            }
            from={{ opacity: 0, scale: 0.92 }}
            animate={{
              opacity: menuMeasured ? 1 : 0,
              scale: menuMeasured ? 1 : 0.92,
            }}
            transition={{ type: 'timing', duration: 140 }}
            style={{ position: 'absolute', top: menuTop, left: menuLeft }}
          >
            <MessageActionMenu
              message={message}
              isMine={isMine}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              close={close}
            />
          </MotiView>
        </>
      ) : (
        /* Expanded: the full emoji grid above the bubble. */
        <MotiView
          from={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'timing', duration: 140 }}
          style={{ position: 'absolute', top: gridTop, left: gridLeft }}
        >
          <MessageEmojiGridPanel
            width={gridWidth}
            height={gridHeight}
            myReacted={myReacted}
            onReact={emoji => close(() => onReact(message, emoji))}
          />
        </MotiView>
      )}
    </View>
  );
};
